import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMobileRedirect } from '@/hooks/useMobileRedirect';

// Map desktop routes to their mobile equivalents
const MOBILE_ROUTE_MAP: Record<string, string> = {
  '/calculator': '/calculator-mobile',
  '/smartbuy-analyzer': '/mobile-smartbuy-analyzer',
  '/investor-analytics': '/mobile-investor-analytics',
  '/homequest': '/mobile-homequest',
  '/chatbot': '/mobile-clara-go',
  '/analytics': '/mobile-analytics',
  '/unified-calculator': '/mobile-unified-calculator',
  '/reverse-piti-calculator': '/mobile-reverse-piti-calculator',
};

// Reverse map for desktop preference on mobile pages
const DESKTOP_ROUTE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(MOBILE_ROUTE_MAP).map(([desktop, mobile]) => [mobile, desktop])
);

interface MobileRouteRedirectProps {
  children: React.ReactNode;
  /** Optional: override the target mobile route */
  mobileRoute?: string;
}

export const MobileRouteRedirect: React.FC<MobileRouteRedirectProps> = ({ 
  children, 
  mobileRoute 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { shouldUseMobile, userPreference } = useMobileRedirect();
  
  useEffect(() => {
    // Only redirect if user hasn't set a manual preference
    if (userPreference !== 'auto') return;
    
    const currentPath = location.pathname;
    
    if (shouldUseMobile) {
      // On mobile, redirect to mobile route if available
      const targetMobileRoute = mobileRoute || MOBILE_ROUTE_MAP[currentPath];
      if (targetMobileRoute && currentPath !== targetMobileRoute) {
        navigate(targetMobileRoute, { replace: true });
      }
    }
  }, [shouldUseMobile, location.pathname, navigate, mobileRoute, userPreference]);
  
  return <>{children}</>;
};

interface DesktopRouteRedirectProps {
  children: React.ReactNode;
  /** Optional: override the target desktop route */
  desktopRoute?: string;
}

export const DesktopRouteRedirect: React.FC<DesktopRouteRedirectProps> = ({ 
  children, 
  desktopRoute 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { shouldUseMobile, userPreference } = useMobileRedirect();
  
  useEffect(() => {
    // Only redirect if user hasn't set a manual preference
    if (userPreference !== 'auto') return;
    
    const currentPath = location.pathname;
    
    if (!shouldUseMobile) {
      // On desktop, redirect to desktop route if available
      const targetDesktopRoute = desktopRoute || DESKTOP_ROUTE_MAP[currentPath];
      if (targetDesktopRoute && currentPath !== targetDesktopRoute) {
        navigate(targetDesktopRoute, { replace: true });
      }
    }
  }, [shouldUseMobile, location.pathname, navigate, desktopRoute, userPreference]);
  
  return <>{children}</>;
};

// Helper to get mobile route for a given path
export function getMobileRoute(path: string): string | undefined {
  return MOBILE_ROUTE_MAP[path];
}

// Helper to get desktop route for a given path
export function getDesktopRoute(path: string): string | undefined {
  return DESKTOP_ROUTE_MAP[path];
}
